import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { sentrySetup } from './helpers/sentry';
import './index.css';
import App from './App';
import { store, persistor } from './configureStore';
import ErrorBoundary from './Components/Global/ErrorBoundary';
import CustomThemeProvider from './elements/theme/CustomThemeProvider';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { isProd, isSandbox } from './helpers/environment';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { FeatureFlagProvider } from './toggle_tools/featureFlagTools';
import LocalToaster from './utils/LocalToaster';
import InitSalesforceChat from './utils/salesforce/InitSalesforceChat';
import * as Sentry from '@sentry/react';

const container = document.getElementById('root');
const root = createRoot(container, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

const queryClient = new QueryClient();

sentrySetup();

(async () => {
  const ldClientId =
    isProd() || isSandbox() ? '620430a5b3158915332e07ba' : import.meta.env.VITE_APP_LAUNCH_DARKLY_CLIENT_ID;
  const LaunchDarklyProvider = await asyncWithLDProvider({
    clientSideID: ldClientId,
  });
  root.render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <CustomThemeProvider>
            <ErrorBoundary>
              <LaunchDarklyProvider>
                <FeatureFlagProvider>
                  <App />
                  <ReactQueryDevtools />
                  <InitSalesforceChat />
                </FeatureFlagProvider>
              </LaunchDarklyProvider>
            </ErrorBoundary>
          </CustomThemeProvider>
        </QueryClientProvider>
      </PersistGate>
      <LocalToaster position="bottom-right" />
    </Provider>
  );
})();
